import React, { useState } from "react";
import { useResource } from "../hooks";
import { api, fmtINR, fmtNum } from "../api";
import AskOIM from "../components/AskOIM";
import { ChevronRight, ChevronDown } from "lucide-react";

const FILTERS = ["All", "Energy", "Resource", "Yield", "Carbon", "Reliability"];

export default function OpportunityCenter() {
  const data = useResource(() => api.opportunities(), [], 3000);
  const [filter, setFilter] = useState("All");
  const [openKey, setOpenKey] = useState(null);

  const ops = (data?.opportunities || []).filter(o => filter === "All" || o.category === filter);
  const totalAnnual = (data?.opportunities || []).reduce((s, o) => s + o.annual_value_inr, 0);

  return (
    <div className="stack" data-testid="opportunity-center">
      <div className="page-title">
        <div>
          <h1>Opportunity Center</h1>
          <div className="sub">McKinsey-style Action Tracker · ranked by annual ₹</div>
        </div>
        <div className="sub mono" style={{ color: "var(--brand-amber-soft)", fontSize: 13 }}>
          Total Pipeline · <strong>{fmtINR(totalAnnual)}</strong>
        </div>
      </div>

      <div className="flex" style={{ gap: 8, flexWrap: "wrap" }}>
        {FILTERS.map(f => (
          <button
            key={f}
            className={"btn" + (filter === f ? " primary" : "")}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f.toLowerCase()}`}
          >{f}</button>
        ))}
      </div>

      <div className="card" data-testid="opportunity-table-card">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 32 }}></th>
              <th>Opportunity</th>
              <th>Category</th>
              <th style={{ textAlign: "right" }}>Impact / hr</th>
              <th style={{ textAlign: "right" }}>Annual Value</th>
              <th style={{ textAlign: "right" }}>Confidence</th>
              <th>Difficulty</th>
              <th>Payback</th>
              <th>Owner</th>
            </tr>
          </thead>
          <tbody>
            {ops.map(o => (
              <React.Fragment key={o.key}>
                <tr
                  className={openKey === o.key ? "expanded" : ""}
                  onClick={() => setOpenKey(k => k === o.key ? null : o.key)}
                  style={{ cursor: "pointer" }}
                  data-testid={`opp-${o.key}`}
                >
                  <td>{openKey === o.key ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                  <td><strong>{o.title}</strong></td>
                  <td><span className={`chip ${o.category.toLowerCase()}`}>{o.category}</span></td>
                  <td className="amber-color" style={{ textAlign: "right" }}>{fmtINR(o.impact_inrh)}</td>
                  <td className="economic" style={{ textAlign: "right" }}>{fmtINR(o.annual_value_inr)}</td>
                  <td className="mono" style={{ textAlign: "right" }}>{Math.round(o.confidence * 100)}%</td>
                  <td className="mono">{o.difficulty}</td>
                  <td className="mono muted">{o.payback}</td>
                  <td className="mono">{o.owner}</td>
                </tr>
                {openKey === o.key && (
                  <tr className="expanded">
                    <td colSpan="9" style={{ padding: "16px 28px" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                        <div>
                          <div className="mono muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6 }}>Problem Statement</div>
                          <div style={{ fontSize: 13, lineHeight: 1.6 }}>{o.problem}</div>
                        </div>
                        <div>
                          <div className="mono muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6 }}>Expected Outcome</div>
                          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                            Closing this gap recovers <strong style={{ color: "var(--brand-green-soft)" }}>{fmtINR(o.annual_value_inr)}/year</strong> at <strong>{Math.round(o.confidence * 100)}%</strong> confidence. Difficulty: {o.difficulty}. Payback window: {o.payback}.
                          </div>
                          <AskOIM question={`Give me an implementation plan for: ${o.title}`} source={o.twin} />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {ops.length === 0 && (
              <tr><td colSpan="9" className="muted" style={{ textAlign: "center", padding: 32 }}>No opportunities in this category.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
